
import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
} from '@backstage/integration-react';
import {
  AnyApiFactory,
  configApiRef,
  createApiFactory,
  discoveryApiRef,
  fetchApiRef,
  oauthRequestApiRef,
  analyticsApiRef,
} from '@backstage/core-plugin-api';
import { OAuth2 } from '@backstage/core-app-api';

import {
  CatalogClient,
  GetEntitiesRequest,
  GetEntitiesResponse,
  CatalogRequestOptions,
} from '@backstage/catalog-client';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

// Cache catalog responses for 10 min to reduce backend load.
const CATALOG_CACHE_TTL_MS = 600_000;
const CATALOG_CACHE_MAX_ENTRIES = 200;
const catalogCache = new Map<string, {
  data: GetEntitiesResponse;
  ts: number;
  inflight?: Promise<GetEntitiesResponse>;
}>();

/** Evicts oldest entries when the cache exceeds the max size. */
function pruneCatalogCache(): void {
  if (catalogCache.size <= CATALOG_CACHE_MAX_ENTRIES) return;
  const excess = catalogCache.size - CATALOG_CACHE_MAX_ENTRIES;
  const iter = catalogCache.keys();
  for (let i = 0; i < excess; i++) {
    catalogCache.delete(iter.next().value!);
  }
}

// Caches getEntities() for 10 min. Only caches non-empty results.
class CachingCatalogClient extends CatalogClient {
  override async getEntities(
    request?: GetEntitiesRequest,
    options?: CatalogRequestOptions,
  ): Promise<GetEntitiesResponse> {
    const key = JSON.stringify(request ?? null);
    const entry = catalogCache.get(key);
    if (entry) {
      if (entry.inflight) return entry.inflight;
      if (Date.now() - entry.ts < CATALOG_CACHE_TTL_MS) return entry.data;
    }
    let resolve!: (v: GetEntitiesResponse) => void;
    let reject!: (e: unknown) => void;
    const inflight = new Promise<GetEntitiesResponse>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    catalogCache.set(key, { ...(entry ?? { data: { items: [] }, ts: 0 }), inflight });
    super.getEntities(request, options).then(result => {
      // Only cache non-empty results — an empty catalog during ingestion should not poison the cache
      if (result.items.length > 0) {
        catalogCache.set(key, { data: result, ts: Date.now() });
        pruneCatalogCache();
      } else {
        catalogCache.delete(key);
      }
      resolve(result);
    }).catch(err => {
      if (catalogCache.get(key)?.inflight === inflight) catalogCache.delete(key);
      reject(err);
    });
    return inflight;
  }
}

// Forces fresh fetches for all subsequent catalog requests.
export function clearCatalogCache(): void {
  catalogCache.clear();
}

import { graviteeAuthApiRef } from './api-refs';
import { notificationsApiRef, NotificationsClient } from '@backstage/plugin-notifications';
import { techRadarApiRef, TechRadarApi, TechRadarLoaderResponse, RadarQuadrant, RadarRing, RadarEntry } from '@backstage-community/plugin-tech-radar';
import { toastApiRef } from '@backstage/frontend-plugin-api';
import { alertApiRef } from '@backstage/core-plugin-api';

/**
 * Module-level re-fetch counter. Incremented after re-seed so the radar
 * re-fetches even when the version id hasn't changed.
 */
let _techRadarSeq = 0;
export function bumpTechRadarSeq() {
  _techRadarSeq += 1;
}



// ── Tech Radar constants ──────────────────────────────────────────────────────
const RADAR_QUADRANTS: RadarQuadrant[] = [
  { id: 'shared-services', name: 'Shared Services' },
  { id: 'tools',           name: 'Tools' },
  { id: 'platforms',       name: 'Platforms' },
  { id: 'techniques',      name: 'Techniques' },
];

const RADAR_RINGS_SIMPLE: RadarRing[] = [
  { id: 'provisioned', name: 'Provisioned', color: '#5BA300', description: 'Proven and recommended for broad use' },
  { id: 'assess',     name: 'Assess',     color: '#C7BA00', description: 'Worth exploring with the goal of understanding impact' },
  { id: 'deprecated', name: 'Deprecated', color: '#E09B96', description: 'We prefer you use something else — due to risks, deprecation, or better alternatives' },
];


function categoryToQuadrant(category: string): string {
  const lower = (category ?? '').toLowerCase().trim();
  if (lower === 'tools' || lower === 'tool') return 'tools';
  if (lower === 'platforms' || lower === 'platform') return 'platforms';
  if (lower.includes('shared')) return 'shared-services';
  if (lower === 'techniques' || lower === 'technique') return 'techniques';
  return 'techniques';
}

function stageToRing(stage: string): string {
  const lower = (stage ?? '').toLowerCase().trim();
  if (lower === 'provisioned' || lower === 'adopt' || lower === 'standard' || lower === 'approved' || lower === 'trial') return 'provisioned';
  if (lower === 'assess') return 'assess';
  if (lower === 'caution' || lower === 'hold' || lower === 'deprecated' || lower === 'contain' || lower === 'retire') return 'deprecated';
  return 'provisioned';
}

function toSlug(name: string): string {
  return (name ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

interface BlipStats { up: number; down: number; commentCount: number; }

/** Build TechRadarLoaderResponse directly from DB items — no MD roundtrip. */
function buildRadarResponse(items: any[], stats: Record<string, BlipStats> = {}): TechRadarLoaderResponse {
  const seen = new Set<string>();
  const entries: RadarEntry[] = [];

  for (const r of items) {
    const id = toSlug(r.technology ?? '');
    if (!id || seen.has(id)) continue;
    seen.add(id);

    const descParts = [r.recommendation, r.costs, r.risks].filter(Boolean);
    const s = stats[id];
    const statsStr = s && (s.up > 0 || s.down > 0 || s.commentCount > 0)
      ? `👍 ${s.up} · 👎 ${s.down} · 💬 ${s.commentCount} comments`
      : '';
    const description = [descParts.join(' · '), statsStr].filter(Boolean).join('\n\n');
    entries.push({
      key: id,
      id,
      title: r.technology,
      quadrant: categoryToQuadrant(r.category),
      description: description || r.area || '',
      timeline: [{ date: new Date(), ringId: stageToRing(r.stage), moved: 0 }],
      links: [],
      url: `/tech-radar/blip/${id}`,
    });
  }

  return { quadrants: RADAR_QUADRANTS, rings: RADAR_RINGS_SIMPLE, entries };
}
// Re-export for convenience
export { graviteeAuthApiRef };


export const apis: AnyApiFactory[] = [
  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),
  ScmAuth.createDefaultApiFactory(),
  createApiFactory({
    api: catalogApiRef,
    deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
    factory: ({ discoveryApi, fetchApi }) =>
      new CachingCatalogClient({
        discoveryApi,
        fetchApi,
      }),
  }),
  createApiFactory({
    api: graviteeAuthApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      oauthRequestApi: oauthRequestApiRef,
      configApi: configApiRef,
    },
    factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
      OAuth2.create({
        discoveryApi,
        oauthRequestApi,
        configApi,
        provider: {
          id: 'oauth2',
          title: 'Gravitee AM',
          icon: () => null,
        },
        environment: configApi.getOptionalString('auth.environment'),
        defaultScopes: ['openid', 'email', 'profile'], 
      }),
  }),
  createApiFactory({
    api: notificationsApiRef,
    deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
    factory: ({ discoveryApi, fetchApi }) => new NotificationsClient({ discoveryApi, fetchApi }),
  }),
  createApiFactory({
    api: techRadarApiRef,
    deps: { discoveryApi: discoveryApiRef },
    factory: ({ discoveryApi }) =>
      ({
        async load(id: string | undefined): Promise<TechRadarLoaderResponse> {
          // id format: "<version>:<seq>" (e.g. "v1.0:0", "latest:1")
          // The :<seq> suffix is used to bust Backstage's useAsync dep cache after re-seed.
          const rawVersion = id?.split(':')[0];
          const version = rawVersion && rawVersion !== 'latest' ? rawVersion : undefined;
          const base = await discoveryApi.getBaseUrl('tech-radar');
          const endpoint = version
            ? `${base}/versions/${encodeURIComponent(version)}`
            : `${base}/items`;
          const [resp, statsResp] = await Promise.all([
            fetch(endpoint),
            fetch(`${base}/blips/stats`).catch(() => null),
          ]);

          let stats: Record<string, BlipStats> = {};
          if (statsResp?.ok) {
            const statsData = await statsResp.json().catch(() => ({}));
            stats = statsData.stats ?? {};
          }

          if (!resp.ok) {
            // Version snapshot not found — fall back to current items
            const fallback = await fetch(`${base}/items`);
            const { items } = await fallback.json();
            return buildRadarResponse(items ?? [], stats);
          }

          const { items } = await resp.json();
          return buildRadarResponse(items ?? [], stats);
        },
      } as TechRadarApi),
  }),
  // Bridge toastApiRef (used by announcements plugin) to alertApiRef so toast
  // notifications show up in the existing Backstage alert banner.
  createApiFactory({
    api: toastApiRef,
    deps: { alertApi: alertApiRef },
    factory: ({ alertApi }) => ({
      post(toast) {
        const severity = toast.status === 'danger' ? 'error'
          : toast.status === 'warning' ? 'warning'
          : toast.status === 'info' ? 'info'
          : 'success';
        const display = toast.timeout !== undefined ? 'transient' : 'permanent';
        alertApi.post({ message: String(toast.title), severity, display });
        return { close: () => {} };
      },
    }),
  }),
  // Analytics — batch events and send to analytics backend plugin
  createApiFactory({
    api: analyticsApiRef,
    deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
    factory: ({ discoveryApi, fetchApi }) => {
      let buffer: Array<{ action: string; subject: string; attributes?: Record<string, any> }> = [];
      let timer: ReturnType<typeof setTimeout> | null = null;

      async function flush() {
        if (buffer.length === 0) return;
        const batch = buffer;
        buffer = [];
        try {
          const baseUrl = await discoveryApi.getBaseUrl('analytics');
          await fetchApi.fetch(`${baseUrl}/events`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(batch),
          });
        } catch {
          // silently drop on failure — analytics should never break the app
        }
      }

      return {
        captureEvent(event) {
          buffer.push({
            action: event.action,
            subject: event.subject,
            attributes: event.attributes as Record<string, any> | undefined,
          });
          if (!timer) {
            timer = setTimeout(() => {
              timer = null;
              flush();
            }, 5000);
          }
        },
      };
    },
  }),
];
